/**
 * The account this browser signed in with, remembered next to the session cookie (HttpOnly, so
 * scripts can't see it). It is a hint, not proof: the dashboard opens with it at once and checks
 * the session meanwhile, `/` sends the browser straight to the dashboard, and public pages offer
 * the dashboard instead of the sign-in.
 */
import type { Account } from './auth';

/** Read by the inline scripts of index.html and of the prerendered `/`, too. */
export const ACCOUNT_KEY = 'lcr.account';
/** On <html> while an account is remembered, so CSS shows the right link labels on first paint. */
const SIGNED_IN = 'data-signed-in';

const textOrNull = (value: unknown) => value === null || typeof value === 'string';

function isAccount(value: unknown): value is Account {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'string' &&
    'role' in value &&
    typeof value.role === 'string' &&
    'name' in value &&
    typeof value.name === 'string' &&
    'username' in value &&
    textOrNull(value.username) &&
    'photo_url' in value &&
    textOrNull(value.photo_url)
  );
}

export function rememberedAccount(): Account | null {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(ACCOUNT_KEY) ?? 'null');
    return isAccount(value) ? value : null;
  } catch {
    return null; // storage is blocked, or the value is not ours
  }
}

/** Puts the page's labels in step with what is remembered (another tab may have changed it). */
export function markSignedIn(): void {
  document.documentElement.toggleAttribute(SIGNED_IN, rememberedAccount() !== null);
}

export function rememberAccount(account: Account): void {
  try {
    localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
  } catch {
    // storage is blocked: the dashboard checks the session every time, as before
  }
  markSignedIn();
}

export function forgetAccount(): void {
  try {
    localStorage.removeItem(ACCOUNT_KEY);
  } catch {
    // nothing was kept
  }
  markSignedIn();
}
